import React from 'react';
import { Link } from 'react-router-dom';
import PublicNavbar from '../components/PublicNavbar';
import PublicFooter from '../components/PublicFooter';
import { useSectionContent } from '../hooks/useSiteContent';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { ArrowRight, Truck, FileText, Briefcase } from 'lucide-react';

const PROGRAMS_DEFAULTS = {
  programs_overview_hero_title: 'Driver Programs',
  programs_overview_hero_subtitle:
    'Three structured tracks: Lease-On, Dispatch-Only, and Company Driver.',
  programs_overview_lease_on_summary:
    'Lease-On Owner-Operator - Run under BlueLock Transport LLC authority. 84% of linehaul to the driver, 16% to BlueLock. Program terms, deductions, and escrow defined in the operating agreement.',
  programs_overview_dispatch_summary:
    'Dispatch-Only - Run under your own authority while we handle load booking, broker communication, and paperwork. Fee is 8% of gross revenue or $400 per week per truck, as defined in your dispatch agreement.',
  programs_overview_company_driver_summary:
    'Company Driver - Operate under BlueLock Transport LLC authority with company-provided equipment. Pay, benefits, and home-time expectations defined in the company driver agreement.',
  programs_overview_disclaimer:
    'No income is guaranteed. Compensation varies by program, experience, and freight availability. Additional compensation may be available for detention, layover, driver assist, multiple stops, TONU, and specialized services when documented and approved.',
};

const ProgramCard = ({ title, body, href, testid, Icon, badge }) => (
  <div
    className="bg-white rounded-2xl border border-gray-200 p-8 flex flex-col"
    data-testid={testid}
  >
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
        <Icon className="w-5 h-5 text-blue-900" />
      </div>
      <div>
        <h3 className="text-xl font-bold text-gray-900">{title}</h3>
        {badge && (
          <span className="inline-block text-xs font-medium text-blue-800 bg-blue-100 px-2 py-0.5 rounded-full mt-1">
            {badge}
          </span>
        )}
      </div>
    </div>
    <p className="text-gray-700 leading-relaxed flex-1">{body}</p>
    <Link
      to={href}
      className="mt-6 inline-flex items-center text-blue-900 font-medium hover:text-blue-700"
      data-testid={`${testid}-link`}
    >
      Program details <ArrowRight className="ml-1 w-4 h-4" />
    </Link>
  </div>
);

const ProgramsPage = () => {
  const { content } = useSectionContent('programs_overview', PROGRAMS_DEFAULTS);
  useDocumentTitle({
    title: 'Driver Programs | BlueLock Freights',
    description:
      'Three driver program tracks at BlueLock Freights: Lease-On (84/16 linehaul split), Dispatch-Only (8% or $400/wk per truck), and Company Driver.',
  });

  return (
    <div className="min-h-screen bg-white" data-testid="programs-page">
      <PublicNavbar />

      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-900 to-blue-800 text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4"
            data-testid="programs-hero-title"
          >
            {content.programs_overview_hero_title}
          </h1>
          <p
            className="text-lg sm:text-xl text-blue-100 max-w-3xl"
            data-testid="programs-hero-subtitle"
          >
            {content.programs_overview_hero_subtitle}
          </p>
        </div>
      </section>

      {/* Program cards */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="programs-grid">
            <ProgramCard
              title="Lease-On"
              badge="Company Authority"
              body={content.programs_overview_lease_on_summary}
              href="/lease-on"
              testid="program-card-lease-on"
              Icon={FileText}
            />
            <ProgramCard
              title="Dispatch-Only"
              badge="Your Authority"
              body={content.programs_overview_dispatch_summary}
              href="/dispatch-services"
              testid="program-card-dispatch-only"
              Icon={Briefcase}
            />
            <ProgramCard
              title="Company Driver"
              badge="Company Equipment"
              body={content.programs_overview_company_driver_summary}
              href="/company-driver"
              testid="program-card-company-driver"
              Icon={Truck}
            />
          </div>

          <div
            className="mt-12 p-6 bg-gray-50 border border-gray-200 rounded-xl"
            data-testid="programs-disclaimer"
          >
            <p className="text-sm text-gray-600">
              {content.programs_overview_disclaimer}
            </p>
          </div>

          <div className="mt-12 text-center">
            <Link
              to="/apply"
              className="inline-flex items-center bg-blue-900 hover:bg-blue-800 text-white px-8 py-3 rounded-lg font-medium transition-colors"
              data-testid="programs-cta-apply"
            >
              Apply Now <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
};

export default ProgramsPage;
